import { useState, useEffect, useMemo } from 'react'
import {
  Text,
  View,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  SafeAreaView,
  Keyboard
} from 'react-native'
import { FontAwesome } from '@expo/vector-icons'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import SelectKeyValue from '../../components/shared/form/SelectKeyValue'

// styles
import { colorPalette, fontPalette, globalStyles } from '../../static/styles/global'

// components
import Card from '../../components/shared/card/Card'
import CustomButton from '../../components/shared/button/Button'
import CardSeparator from '../../components/shared/card/CardSeparator'
import HomeDetailsHeader from '../../components/home/HomeDetailsHeader'

// stores
import { useCommunicateStore } from '../../stores/communicate'
import { useHomeStore } from '../../stores/home'
import { usePersonalStore } from '../../stores/personalInfo'

//translation
import { useTranslation } from 'react-i18next'

const NewMessageScreen = ({ navigation, route }: any): JSX.Element => {
  const { t } = useTranslation()
  const [editMessage, setEditMessage] = useState(false)
  const [subject, setSubject] = useState(t('genaralquestion'))
  const [message, setMessage] = useState('')
  const [inputHeight, setInputHeight] = useState(44)

  // params
  const { params } = route
  const userId = params?.userId || params?.mostRecentUserOnBoardings
  const isEmployeeMessage = params?.isEmployeeMessage

  const homeStore = useHomeStore()
  const personalStore = usePersonalStore()

  // stores' data
  const {
    createNewMessage,
    createEmployeeMessage,
    getEmployeesByDept,
    employees
  } = useCommunicateStore()

  const [selectedEmployee, setSelectedEmployee] = useState<any>(null)

  const getEmployeeName = (emp: any) => {
    if (!emp) return ''
    return emp.name || emp.fullName || (emp.firstName && emp.lastName ? `${emp.firstName} ${emp.lastName}` : emp.firstName || '')
  }

  const employeeList = useMemo(() => {
    return Array.isArray(employees) ? employees : []
  }, [employees])

  const employeeOptions = useMemo(() => {
    return employeeList.map((emp: any) => {
      const name = getEmployeeName(emp)
      const value = emp.essUserOnBoardingKey || emp.essUserKey || 0
      return {
        value: value,
        name: name
      }
    })
  }, [employeeList])

  useEffect(() => {
    if (isEmployeeMessage && userId) {
      if (!personalStore?.personalData) {
        personalStore.fetchData(userId)
      }
      if (!homeStore?.homeData) {
        homeStore.fetchData(userId)
      }
    }
  }, [isEmployeeMessage, personalStore?.personalData, homeStore?.homeData, userId])

  useEffect(() => {
    if (isEmployeeMessage) {
      const entityId = personalStore?.personalData?.entityId
      const storeId = homeStore?.homeData?.storeNumber
      const deptId = homeStore?.homeData?.deptId
      if (entityId && storeId && deptId) {
        getEmployeesByDept(entityId, storeId, deptId)
      }
    }
  }, [
    isEmployeeMessage,
    personalStore?.personalData?.entityId,
    homeStore?.homeData?.storeNumber,
    homeStore?.homeData?.deptId
  ])

  const handleSendMessage = (): void => {
    if (isEmployeeMessage) {
      createEmployeeMessage(userId, {
        receiverEssUserKey: selectedEmployee?.essUserKey || 0,
        receiverEssUserOnBoardingKey: selectedEmployee?.essUserOnBoardingKey || 0,
        subject: subject.trim(),
        message: message.trim()
      })
    } else {
      createNewMessage(userId, {
        message: message.trim(),
        subject: subject.trim()
      })
    }
    setMessage('')
    if (navigation.canGoBack()) {
      navigation.goBack()
    } else {
      navigation.replace('Onboarding')
    }

    Keyboard.dismiss()
  }

  return (
    <KeyboardAwareScrollView keyboardShouldPersistTaps={'always'}>
      <SafeAreaView>
        <View style={[globalStyles.primaryBgColor, globalStyles.containerBand]}>
          <View style={[globalStyles.horizontalPadGap]}>
            <HomeDetailsHeader
              title={t('newmessage')}
              navLabel={t('messeges')}
              navScreen="CommunicateWithAdmin"
            />
            {isEmployeeMessage && (
              <View style={[styles.employeeHeaderContainer]}>
                <View style={{ maxWidth: '100%' }}>
                  <SelectKeyValue
                    title={t('selectemployee')}
                    options={employeeOptions}
                    selectedOption={
                      selectedEmployee
                        ? getEmployeeName(selectedEmployee)
                        : t('selectemployee')
                    }
                    onSelect={(val: any) => {
                      const empObj = employeeList?.find(
                        (emp: any) =>
                          (emp.essUserOnBoardingKey || emp.essUserKey) === val
                      )
                      setSelectedEmployee(empObj)
                    }}
                  />
                </View>
              </View>
            )}
          </View>
        </View>
        <View style={[globalStyles.containerOverlap, globalStyles.mb4]}>
          {!isEmployeeMessage && editMessage && (
            <Card>
              <View style={[styles.labelSubject, globalStyles.mb2]}>
                <Text
                  style={[
                    globalStyles.text,
                    globalStyles.fwBold,
                    globalStyles.fs18,
                    globalStyles.blackTextColor
                  ]}
                >
                  {t('editsubject')}
                </Text>
              </View>
              <View style={[globalStyles.mb3]}>
                <TextInput
                  placeholder={t('subject')}
                  placeholderTextColor={colorPalette.lightGray}
                  style={[
                    globalStyles.input,
                    globalStyles.inputLight,
                    styles.inputSubject
                  ]}
                  blurOnSubmit={false}
                  value={subject}
                  onChangeText={(text) => setSubject(text)}
                />
              </View>
              <CustomButton
                text={t('continuetomessage')}
                onPress={() => {
                  setEditMessage(false)
                }}
                isDisabled={!subject.trim()}
              />
            </Card>
          )}
          {(isEmployeeMessage || !editMessage) && (
            <Card>
              {!isEmployeeMessage && (
                <>
                  <View
                    style={[
                      globalStyles.hDirection,
                      globalStyles.vCenterItems,
                      globalStyles.hBetweenItems
                    ]}
                  >
                    <Text
                      style={[
                        globalStyles.text,
                        globalStyles.fwBold,
                        globalStyles.fs18,
                        globalStyles.blackTextColor
                      ]}
                    >
                      {subject}
                    </Text>
                    <TouchableOpacity
                      onPress={() => {
                        setEditMessage(!editMessage)
                      }}
                    >
                      <Text
                        style={[
                          globalStyles.text,
                          globalStyles.fs12,
                          globalStyles.secondaryTextColor,
                          globalStyles.textUnderline
                        ]}
                      >
                        {t('editsubject')}
                      </Text>
                    </TouchableOpacity>
                  </View>
                  <CardSeparator light={true} />
                </>
              )}
              <View style={[styles.msgWrapper]}>
                <View style={[styles.footer, styles.noBorder]}>
                  <View
                    style={[globalStyles.flexOne, styles.messageInputWrapper]}
                  >
                    <TextInput
                      multiline
                      style={[
                        styles.input,
                        styles.messageInput,
                        { height: Math.min(100, Math.max(44, inputHeight)) }
                      ]}
                      placeholder={t('message')}
                      placeholderTextColor={colorPalette.lightGray}
                      value={message}
                      onChangeText={(text) => setMessage(text)}
                      onContentSizeChange={(event) => {
                        setInputHeight(event.nativeEvent.contentSize.height)
                      }}
                    />
                    <TouchableOpacity
                      style={[styles.messageSend]}
                      onPress={handleSendMessage}
                      disabled={
                        message.trim() === '' ||
                        (isEmployeeMessage && !selectedEmployee)
                      }
                    >
                      <FontAwesome
                        name="send"
                        size={26}
                        color={colorPalette.secondary}
                        style={[
                          (message.trim() === '' ||
                            (isEmployeeMessage && !selectedEmployee)) && {
                            opacity: 0.3
                          }
                        ]}
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </Card>
          )}
        </View>
      </SafeAreaView>
    </KeyboardAwareScrollView>
  )
}

const styles = StyleSheet.create({
  labelSubject: {
    borderLeftWidth: 2,
    borderLeftColor: colorPalette.secondary,
    paddingLeft: 10
  },
  inputSubject: {
    borderWidth: 0,
    borderRadius: 0,
    borderBottomColor: colorPalette.darkLight,
    borderBottomWidth: 1,
    paddingHorizontal: 5
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: '100%',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderTopWidth: 1,
    borderColor: colorPalette.darkLight
  },
  noBorder: {
    borderColor: 'transparent'
  },
  msgWrapper: {
    marginHorizontal: -10,
    marginVertical: -10
  },
  messageInput: {
    textAlignVertical: 'top',
    borderColor: colorPalette.lightGray,
    borderRadius: 50,
    minHeight: 44,
    paddingHorizontal: 20,
    paddingRight: 60,
    paddingTop: 11
  },
  messageInputWrapper: {
    position: 'relative'
  },
  messageSend: {
    position: 'absolute',
    right: 18,
    top: '50%',
    marginTop: -11
  },
  employeeHeaderContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: 10,
    marginBottom: 10,
    width: '100%'
  },
  input: {
    borderWidth: 1,
    backgroundColor: colorPalette.white,
    borderColor: colorPalette.gray,
    color: colorPalette.dark,
    paddingHorizontal: 12,
    // height: 40,
    fontFamily: fontPalette.primaryRegular,
    fontSize: 15,
    lineHeight: 20,
    borderRadius: 8,
    marginVertical: 4
  },
})

export default NewMessageScreen

